import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';

const FamilyAcceptPage = () => {
  const { token }  = useParams();
  const { user, refreshUser } = useAuth();
  const navigate   = useNavigate();

  const [preview,    setPreview]    = useState(null);
  const [myData,     setMyData]     = useState(null);   // { childCount, storyCount }
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [accepting,  setAccepting]  = useState(false);
  const [done,       setDone]       = useState(false);
  const [confirmed,  setConfirmed]  = useState(false);  // user ticked the "I understand" box

  // Load the invite preview (public — works before login)
  useEffect(() => {
    API.get(`/family/invite/${token}`)
      .then(res => setPreview(res.data.invite))
      .catch(err => setError(err.response?.data?.message || 'This invite is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  // Once the user is logged in AND the preview loaded, check if they have existing data
  useEffect(() => {
    if (!user || !preview) return;
    API.get(`/family/invite/${token}/my-data`)
      .then(res => setMyData(res.data))
      .catch(() => setMyData({ childCount: 0, storyCount: 0 })); // silent fail — don't block the flow
  }, [user, preview, token]);

  const hasExistingData = myData && (myData.childCount > 0 || myData.storyCount > 0);
  // Only require the checkbox if user has existing data to be hidden
  const needsConfirmation = hasExistingData && !confirmed;

  const handleAccept = async () => {
    if (!user) {
      navigate(`/login?redirect=/family/accept/${token}`);
      return;
    }
    setAccepting(true); setError('');
    try {
      await API.post(`/family/invite/${token}/accept`);
      await refreshUser();
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept invite. Please try again.');
    } finally { setAccepting(false); }
  };

  const roleLabel = (role) =>
    role === 'can_generate' ? 'view and generate stories' : 'view stories';

  // Helper: build a plain-English summary of what will be hidden
  const hiddenSummary = () => {
    if (!myData) return '';
    const parts = [];
    if (myData.childCount > 0)
      parts.push(`${myData.childCount} child profile${myData.childCount !== 1 ? 's' : ''}`);
    if (myData.storyCount > 0)
      parts.push(`${myData.storyCount} stor${myData.storyCount !== 1 ? 'ies' : 'y'}`);
    return parts.join(' and ');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg,#f0e8ff 0%,#e8d5ff 50%,#fce7f3 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
    }}>
      <div style={{
        maxWidth: '460px', width: '100%',
        background: 'var(--card-bg)',
        borderRadius: '24px',
        boxShadow: '0 20px 60px rgba(111,66,193,0.15)',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#6f42c1,#9b59b6)', padding: '32px 32px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🌙</div>
          <h2 style={{ color: '#fff', fontWeight: 700, margin: 0, fontSize: '22px' }}>Family Invitation</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', margin: '8px 0 0', fontSize: '14px' }}>Dreamy Bedtime</p>
        </div>

        <div style={{ padding: '32px' }}>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <span className="spinner-border" style={{ color: '#6f42c1' }}></span>
            </div>
          )}

          {/* Invalid / expired */}
          {error && !loading && !accepting && !done && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>😕</div>
              <p style={{ color: '#dc3545', fontWeight: 600, marginBottom: '8px' }}>Invite unavailable</p>
              <p style={{ color: 'var(--text-muted2)', fontSize: '14px', marginBottom: '24px' }}>{error}</p>
              <Link to="/" className="btn btn-dream rounded-pill px-4">Go to Home</Link>
            </div>
          )}

          {/* Success */}
          {done && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
              <p style={{ fontWeight: 700, fontSize: '18px', marginBottom: '8px', color: 'var(--text-main)' }}>
                You're in!
              </p>
              <p style={{ color: 'var(--text-muted2)', fontSize: '14px', marginBottom: '24px' }}>
                You now have access to <strong style={{ color: 'var(--text-main)' }}>{preview?.inviterName}</strong>'s family library.
              </p>
              {hasExistingData && (
                <div style={{
                  background: 'var(--header-bg)',
                  border: '1px solid var(--border-purple)',
                  borderRadius: '12px',
                  padding: '14px 16px',
                  marginBottom: '20px',
                  fontSize: '13px',
                  color: 'var(--text-muted2)',
                  textAlign: 'left',
                }}>
                  <i className="bi bi-info-circle me-2" style={{ color: '#6f42c1' }}></i>
                  Your own {hiddenSummary()} {myData.childCount + myData.storyCount === 1 ? 'is' : 'are'} safely stored and will reappear if you ever leave this family.
                </div>
              )}
              <button className="btn btn-dream rounded-pill px-5 fw-semibold" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </button>
            </div>
          )}

          {/* Main accept flow */}
          {preview && !done && !loading && (
            <>
              {/* Who invited you + role */}
              <p style={{ color: 'var(--text-muted2)', fontSize: '14px', textAlign: 'center', marginBottom: '20px' }}>
                <strong style={{ color: 'var(--text-main)' }}>{preview.inviterName}</strong> has invited you to join their
                family on Dreamy Bedtime. You'll be able to <strong style={{ color: 'var(--text-main)' }}>{roleLabel(preview.role)}</strong>.
              </p>

              {/* Access + expiry tiles */}
              <div style={{ background: 'var(--purple-section)', borderRadius: '16px', padding: '18px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', background: 'var(--card-bg)', borderRadius: '12px' }}>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>
                      {preview.role === 'can_generate' ? '✏️' : '📖'}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-main)' }}>
                      {preview.role === 'can_generate' ? 'View & Generate' : 'View Only'}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)' }}>access level</div>
                  </div>
                  <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', background: 'var(--card-bg)', borderRadius: '12px' }}>
                    <div style={{ fontSize: '20px', marginBottom: '4px' }}>📅</div>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-main)' }}>
                      {new Date(preview.expiresAt).toLocaleDateString()}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted2)' }}>invite expires</div>
                  </div>
                </div>
                <p style={{ fontSize: '12px', color: 'var(--text-muted2)', margin: 0, textAlign: 'center' }}>
                  You'll share access to the same children's profiles and story library.
                </p>
              </div>

              {/* ── Warning: user has existing data that will be hidden ── */}
              {user && hasExistingData && (
                <div style={{
                  background: '#fffbeb',
                  border: '1.5px solid #fcd34d',
                  borderRadius: '14px',
                  padding: '16px',
                  marginBottom: '20px',
                }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <span style={{ fontSize: '20px', flexShrink: 0 }}>⚠️</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '14px', color: '#92400e', marginBottom: '4px' }}>
                        Your existing data will be hidden
                      </div>
                      <p style={{ fontSize: '13px', color: '#78350f', margin: 0, lineHeight: 1.5 }}>
                        You currently have <strong>{hiddenSummary()}</strong> on your own account.
                        While you're a co-parent, your dashboard will show{' '}
                        <strong>{preview.inviterName}</strong>'s family instead.
                        Your own data won't be deleted — it'll come back the moment you leave.
                      </p>
                    </div>
                  </div>
                  <label style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px',
                    cursor: 'pointer', fontSize: '13px', color: '#78350f',
                  }}>
                    <input
                      type="checkbox"
                      checked={confirmed}
                      onChange={e => setConfirmed(e.target.checked)}
                      style={{ marginTop: '2px', accentColor: '#6f42c1', flexShrink: 0 }}
                    />
                    I understand my {hiddenSummary()} will be hidden while I'm a co-parent
                  </label>
                </div>
              )}

              {/* API error */}
              {error && (
                <div className="alert alert-danger py-2 small mb-3 rounded-3">{error}</div>
              )}

              {/* Not logged in note */}
              {!user && (
                <p style={{ fontSize: '13px', color: 'var(--text-muted2)', textAlign: 'center', marginBottom: '16px' }}>
                  You need to be logged in to accept this invite.
                </p>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  className="btn btn-dream rounded-pill w-100 py-2 fw-semibold"
                  onClick={handleAccept}
                  disabled={accepting || needsConfirmation}
                  title={needsConfirmation ? 'Please tick the checkbox above to confirm you understand' : ''}
                >
                  {accepting
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Accepting…</>
                    : user ? 'Accept Invitation' : 'Log In to Accept'}
                </button>
                <button className="btn btn-outline-secondary rounded-pill w-100"
                  onClick={() => navigate('/')}>
                  Decline
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default FamilyAcceptPage;
